import React, { useState } from 'react';
import {
  Box,
  Typography,
  Grid,
  List,
  ListItem,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  FileText as FileTextIcon,
  Target as TargetIcon,
  Lightbulb as VisionIcon,
  TrendingUp as KPIIcon,
  DollarSign as CostIcon,
  CheckCircle as ObjectiveIcon,
  Rocket as ProductIcon,
  Users as UserResearchIcon,
  GitBranch as RoadmapIcon,
  Package as FeaturesIcon,
  Zap as AgileIcon,
  Code as TechIcon,
  Database as BackendIcon,
  Layout as FrontendIcon,
  Cloud as InfraIcon,
  Shield as SecurityIcon,
  Gauge as PerformanceIcon,
  GitBranch as DevOpsIcon,
  BarChart as MarketIcon,
  TrendingUp as GrowthIcon,
  PieChart as CompetitionIcon,
  Globe as ChannelIcon,
  Award as PositioningIcon,
  Crosshair as SegmentationIcon,
  Users2 as TargetMarketIcon,
  Users as TeamIcon,
  Briefcase as RoleIcon,
  GraduationCap as EducationIcon,
} from 'lucide-react';
import HolographicStepper, { StepData } from '../common/HolographicStepper.tsx';
import GradientText from '../common/GradientText.tsx';
import EditableContent from './EditableContent.tsx';
//import ProblemSolvingToolbox from '../../pages/Home/components/ProblemSolvingToolbox.tsx';

interface PlanStepperProps {
  problemStatement?: string;
  initialStep?: number;
  onComplete?: () => void;
  onSubmitRFP?: () => void;
  onBack?: () => void;
}

const ContentBox = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3),
  background: 'rgba(26, 26, 46, 0.4)',
  borderRadius: theme.spacing(1),
  border: '1px solid rgba(138, 43, 226, 0.2)',
  marginBottom: theme.spacing(3),
  position: 'relative',
  overflowX: 'hidden',
  transition: 'all 0.3s ease',
  '&:hover': {
    border: '1px solid rgba(138, 43, 226, 0.4)',
    boxShadow: '0 4px 12px rgba(138, 43, 226, 0.1)',
  }
}));

const CostEstimateBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: theme.spacing(2),
  background: 'rgba(0, 204, 153, 0.1)',
  borderRadius: theme.spacing(1),
  border: '1px solid rgba(0, 204, 153, 0.3)',
  marginBottom: theme.spacing(2),
}));

const StepperContainer = styled(Box)(({ theme }) => ({
  position: 'relative',
  width: '100%',
  height: '100%',
  display: 'flex',
  //flexDirection: 'column',
  padding: theme.spacing(3),
  overflow: 'auto',
}));

const Section = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(4),
  '&:last-child': {
    marginBottom: 0,
  }
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  color: '#fff',
  marginBottom: theme.spacing(2),
  borderBottom: '1px solid rgba(138, 43, 226, 0.2)',
  paddingBottom: theme.spacing(1),
}));

const ContentCard = styled(Box)(({ theme }) => ({
  background: 'rgba(26, 26, 46, 0.6)',
  backdropFilter: 'blur(10px)',
  borderRadius: theme.spacing(2),
  border: '1px solid rgba(138, 43, 226, 0.2)',
  padding: theme.spacing(3),
  marginTop: theme.spacing(3),
  boxShadow: '0 4px 30px rgba(0, 0, 0, 0.1)',
  height: 'calc(100% - 100px)',
  display: 'flex',
  flexDirection: 'column',
  flex: 1,
  overflowY: 'auto',
  overflowX: 'hidden',
  '&::-webkit-scrollbar': {
    width: '8px',
  },
  '&::-webkit-scrollbar-track': {
    background: 'rgba(26, 26, 46, 0.4)',
    borderRadius: '4px',
  },
  '&::-webkit-scrollbar-thumb': {
    background: 'rgba(138, 43, 226, 0.3)',
    borderRadius: '4px',
    '&:hover': {
      background: 'rgba(138, 43, 226, 0.5)',
    },
  },
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '2px',
    background: 'linear-gradient(90deg, #003366, #00CC99, #8A2BE2, #FF6F00)',
    opacity: 1,
  },
}));

const PlanStepper: React.FC<PlanStepperProps> = ({
  problemStatement = 'Define your problem here...',
  initialStep = 0,
  onComplete,
  onSubmitRFP,
  onBack,
}) => {
  // State for Executive Summary (Step 1)
  const [executiveSummary, setExecutiveSummary] = useState({
    overview: {
      title: 'Plan Overview',
      content: problemStatement,
      icon: FileTextIcon,
    },
    costEstimate: {
      estimated: 0,
      solution: 0,
      actual: 0,
    },
    mission: {
      title: 'Mission & Vision',
      content: 'Define your mission and vision statements here...',
      icon: VisionIcon,
    },
    objectives: {
      title: 'Key Objectives',
      items: [
        { id: 1, content: 'Define first key objective...', icon: ObjectiveIcon },
        { id: 2, content: 'Define second key objective...', icon: ObjectiveIcon },
      ],
    },
    kpis: {
      title: 'Key Performance Indicators',
      items: [
        { id: 1, content: 'Define first KPI...', icon: KPIIcon },
        { id: 2, content: 'Define second KPI...', icon: KPIIcon },
      ],
    },
  });

  // Add Technology Roadmap state
  const [techRoadmap, setTechRoadmap] = useState({
    overview: {
      title: 'Technology Stack Overview',
      content: 'Define the overall technology architecture and stack...',
      icon: TechIcon,
      cost: 4500,
    },
    frontend: {
      title: 'Frontend Architecture',
      stack: [
        {
          id: 'ui',
          name: 'UI Framework',
          description: 'React with TypeScript for type-safe development',
          icon: FrontendIcon,
        },
        {
          id: 'state',
          name: 'State Management',
          description: 'Redux Toolkit with RTK Query for API integration',
          icon: FrontendIcon,
        },
      ],
      cost: 3800,
    },
    backend: {
      title: 'Backend Infrastructure',
      stack: [
        {
          id: 'api',
          name: 'API Layer',
          description: 'Node.js with Express and GraphQL',
          icon: BackendIcon,
        },
        {
          id: 'database',
          name: 'Database',
          description: 'PostgreSQL with Redis caching',
          icon: BackendIcon,
        },
      ],
      cost: 4200,
    },
    infrastructure: {
      title: 'Cloud Infrastructure',
      components: [
        {
          id: 'hosting',
          name: 'Hosting',
          description: 'AWS ECS with auto-scaling',
          icon: InfraIcon,
        },
        {
          id: 'storage',
          name: 'Storage',
          description: 'S3 for assets, RDS for databases',
          icon: InfraIcon,
        },
      ],
      cost: 5500,
    },
    security: {
      title: 'Security & Compliance',
      measures: [
        {
          id: 'auth',
          name: 'Authentication & Authorization',
          description: 'OAuth 2.0 with JWT tokens',
          icon: SecurityIcon,
        },
        {
          id: 'encryption',
          name: 'Data Encryption',
          description: 'AES-256 encryption at rest and in transit',
          icon: SecurityIcon,
        },
      ],
      cost: 3900,
    },
    performance: {
      title: 'Performance Optimization',
      strategies: [
        {
          id: 'caching',
          name: 'Caching Strategy',
          description: 'Multi-layer caching with CDN',
          icon: PerformanceIcon,
        },
        {
          id: 'optimization',
          name: 'Code Optimization',
          description: 'Automated performance monitoring and optimization',
          icon: PerformanceIcon,
        },
      ],
      cost: 3200,
    },
    devops: {
      title: 'DevOps & CI/CD',
      pipeline: [
        {
          id: 'ci',
          name: 'Continuous Integration',
          description: 'Automated testing and build pipeline',
          icon: DevOpsIcon,
        },
        {
          id: 'cd',
          name: 'Continuous Deployment',
          description: 'Automated deployment with rollback capability',
          icon: DevOpsIcon,
        },
      ],
      cost: 4100,
    },
  });

  // Add Product Roadmap state
  const [productRoadmap, setProductRoadmap] = useState({
    vision: {
      title: 'Product Vision & Strategy',
      content: 'Define the product vision, goals, and strategic direction...',
      icon: ProductIcon,
      cost: 2800,
    },
    userResearch: {
      title: 'User Research & Personas',
      personas: [
        {
          id: 1,
          title: 'Primary Persona',
          description: 'Define primary user persona...',
          icon: UserResearchIcon,
        },
        {
          id: 2,
          title: 'Secondary Persona',
          description: 'Define secondary user persona...',
          icon: UserResearchIcon,
        },
      ],
      cost: 3400,
    },
    roadmap: {
      title: 'Product Roadmap',
      quarters: [
        {
          id: 'Q1',
          title: 'Foundation',
          objectives: [
            'Core platform development',
            'Basic functionality',
            'Initial testing',
          ],
          icon: RoadmapIcon,
        },
        {
          id: 'Q2',
          title: 'Expansion',
          objectives: [
            'Advanced features',
            'Integration capabilities',
            'Beta program',
          ],
          icon: RoadmapIcon,
        },
        {
          id: 'Q3',
          title: 'Refinement',
          objectives: [
            'Performance optimization',
            'User experience enhancements',
            'Market launch preparation',
          ],
          icon: RoadmapIcon,
        },
        {
          id: 'Q4',
          title: 'Scaling',
          objectives: [
            'Enterprise features',
            'Advanced analytics',
            'Market expansion',
          ],
          icon: RoadmapIcon,
        },
      ],
      cost: 3800,
    },
    features: {
      title: 'Feature Prioritization',
      categories: [
        {
          id: 'must-have',
          title: 'Must-Have Features',
          items: ['Feature 1', 'Feature 2'],
          icon: FeaturesIcon,
        },
        {
          id: 'high-value',
          title: 'High-Value Features',
          items: ['Feature 3', 'Feature 4'],
          icon: FeaturesIcon,
        },
      ],
      cost: 3200,
    },
    agile: {
      title: 'Agile Development Process',
      processes: [
        {
          id: 'sprints',
          title: 'Sprint Planning',
          description: 'Two-week sprint cycles with defined goals...',
          icon: AgileIcon,
        },
        {
          id: 'ceremonies',
          title: 'Agile Ceremonies',
          description: 'Daily standups, sprint reviews, and retrospectives...',
          icon: AgileIcon,
        },
      ],
      cost: 2600,
    },
  });

  // Add Market Strategy & Analysis state
  const [marketStrategy, setMarketStrategy] = useState({
    overview: {
      title: 'Market Strategy Overview',
      content: 'Define the overall market approach and positioning...',
      icon: MarketIcon,
      cost: 4200,
    },
    positioning: {
      title: 'Market Positioning',
      strategies: [
        {
          id: 'value',
          name: 'Value Proposition',
          description: 'Unique value offering in the market...',
          icon: PositioningIcon,
        },
        {
          id: 'differentiation',
          name: 'Differentiation Strategy',
          description: 'Key differentiators from competitors...',
          icon: PositioningIcon,
        },
      ],
      cost: 3500,
    },
    channels: {
      title: 'Marketing Channels',
      strategies: [
        {
          id: 'digital',
          name: 'Digital Marketing',
          description: 'Online presence and digital campaigns...',
          icon: ChannelIcon,
        },
        {
          id: 'traditional',
          name: 'Traditional Marketing',
          description: 'Offline marketing strategies...',
          icon: ChannelIcon,
        },
      ],
      cost: 5200,
    },
    marketAnalysis: {
      overview: {
        title: 'Market Analysis Overview',
        content: 'Comprehensive market research and insights...',
        icon: GrowthIcon,
        cost: 4800,
      },
      targetMarket: {
        title: 'Target Market Analysis',
        segments: [
          {
            id: 'primary',
            name: 'Primary Market',
            description: 'Core customer segment analysis...',
            size: '5M users',
            growth: '15% YoY',
            icon: TargetMarketIcon,
          },
          {
            id: 'secondary',
            name: 'Secondary Market',
            description: 'Additional market opportunities...',
            size: '3M users',
            growth: '12% YoY',
            icon: TargetMarketIcon,
          },
        ],
        cost: 3800,
      },
      competition: {
        title: 'Competitive Analysis',
        competitors: [
          {
            id: 'direct',
            name: 'Direct Competitors',
            description: 'Primary market competitors...',
            analysis: 'Strengths and weaknesses...',
            icon: CompetitionIcon,
          },
          {
            id: 'indirect',
            name: 'Indirect Competitors',
            description: 'Secondary market competitors...',
            analysis: 'Market overlap and differentiation...',
            icon: CompetitionIcon,
          },
        ],
        cost: 4200,
      },
      segmentation: {
        title: 'Market Segmentation',
        segments: [
          {
            id: 'demographic',
            name: 'Demographic Segmentation',
            description: 'Age, income, location analysis...',
            icon: SegmentationIcon,
          },
          {
            id: 'behavioral',
            name: 'Behavioral Segmentation',
            description: 'Usage patterns and preferences...',
            icon: SegmentationIcon,
          },
        ],
        cost: 3600,
      },
    },
  });

  // Add Financial Plan state
  const [financialPlan, setFinancialPlan] = useState({
    overview: {
      title: 'Financial Overview',
      content: 'Financial strategy and projections overview...',
      icon: CostIcon,
      cost: 5500,
    },
    revenue: {
      title: 'Revenue Projections',
      projections: [
        {
          id: 'year1',
          period: 'Year 1',
          amount: 1000000,
          growth: '25%',
          description: 'Initial market penetration and customer acquisition...',
          icon: KPIIcon,
        },
        {
          id: 'year2',
          period: 'Year 2',
          amount: 2500000,
          growth: '150%',
          description: 'Market expansion and product scaling...',
          icon: KPIIcon,
        },
      ],
      cost: 3800,
    },
    costs: {
      title: 'Cost Structure',
      categories: [
        {
          id: 'fixed',
          name: 'Fixed Costs',
          items: [
            { name: 'Salaries', amount: 500000 },
            { name: 'Infrastructure', amount: 200000 },
          ],
          icon: CostIcon,
        },
        {
          id: 'variable',
          name: 'Variable Costs',
          items: [
            { name: 'Marketing', amount: 300000 },
            { name: 'Operations', amount: 250000 },
          ],
          icon: CostIcon,
        },
      ],
      cost: 4200,
    },
    funding: {
      title: 'Funding Requirements',
      rounds: [
        {
          id: 'seed',
          name: 'Seed Round',
          amount: 1000000,
          description: 'Initial product development and market entry...',
          icon: CostIcon,
        },
        {
          id: 'seriesA',
          name: 'Series A',
          amount: 5000000,
          description: 'Scaling operations and market expansion...',
          icon: CostIcon,
        },
      ],
      cost: 3500,
    },
    metrics: {
      title: 'Financial Metrics',
      kpis: [
        {
          id: 'burn',
          name: 'Burn Rate',
          value: '$100k/month',
          description: 'Current operational expenses...',
          icon: KPIIcon,
        },
        {
          id: 'runway',
          name: 'Runway',
          value: '18 months',
          description: 'Available operating time with current funding...',
          icon: KPIIcon,
        },
      ],
      cost: 2800,
    },
  });

  // Add Company & Team state
  const [companyTeam, setCompanyTeam] = useState({
    overview: {
      title: 'Company & Team Overview',
      content: 'Overview of company structure and key team members...',
      icon: TeamIcon,
      cost: 4200,
    },
    leadership: {
      title: 'Leadership Team',
      members: [
        {
          id: 'ceo',
          name: 'John Smith',
          role: 'CEO & Co-Founder',
          bio: '20+ years experience in software and AI...',
          achievements: ['Previous successful exit', 'Industry thought leader'],
          icon: RoleIcon,
        },
        {
          id: 'cto',
          name: 'Jane Doe',
          role: 'CTO & Co-Founder',
          bio: 'ML expert with background in enterprise systems...',
          achievements: ['PhD in Machine Learning', '15 patents'],
          icon: RoleIcon,
        },
      ],
      cost: 3500,
    },
    departments: {
      title: 'Department Structure',
      teams: [
        {
          id: 'engineering',
          name: 'Engineering',
          headCount: 12,
          roles: ['Senior Engineers', 'Full-stack Developers', 'ML Engineers'],
          icon: TeamIcon,
        },
        {
          id: 'product',
          name: 'Product & Design',
          headCount: 8,
          roles: ['Product Managers', 'UX Designers', 'UI Developers'],
          icon: TeamIcon,
        },
      ],
      cost: 2800,
    },
    hiring: {
      title: 'Hiring Plan',
      plans: [
        {
          id: 'q1-q2',
          period: 'Q1-Q2 2024',
          roles: ['Senior ML Engineer', 'Full-stack Developer', 'Product Manager'],
          budget: 450000,
          icon: RoleIcon,
        },
        {
          id: 'q3-q4',
          period: 'Q3-Q4 2024',
          roles: ['DevOps Engineer', 'UX Designer', 'Sales Director'],
          budget: 380000,
          icon: RoleIcon,
        },
      ],
      cost: 3200,
    },
    culture: {
      title: 'Company Culture & Values',
      values: [
        {
          id: 'innovation',
          name: 'Innovation First',
          description: 'Embracing cutting-edge technology and creative solutions...',
          icon: EducationIcon,
        },
        {
          id: 'collaboration',
          name: 'Collaborative Spirit',
          description: 'Fostering an environment of open communication...',
          icon: EducationIcon,
        },
      ],
      cost: 2500,
    },
  });

  // Handler for generating content with AI
  const handleGenerate = async (section: string) => {
    // This would be your API call to the LLM
    console.log(`Generating content for ${section}`);
    // Example API call:
    // const response = await fetch('/api/generate', {
    //   method: 'POST',
    //   body: JSON.stringify({ section, problemStatement }),
    // });
    // const data = await response.json();
    // Update the relevant section with the generated content
  };

  const [activeStep, setActiveStep] = useState<number>(initialStep);

  const ExecutiveSummaryContent = () => (
    <Section>
      <SectionTitle variant="h5">
        Executive Summary
      </SectionTitle>

      <Section>
        {/* Plan Overview */}
        <EditableContent
          title={executiveSummary.overview.title}
          content={executiveSummary.overview.content}
          icon={executiveSummary.overview.icon}
          onUpdate={(newData) => {
            setExecutiveSummary(prev => ({
              ...prev,
              overview: { ...prev.overview, ...newData },
            }));
          }}
          onGenerate={() => handleGenerate('overview')}
        />
      </Section>

      <Section>
        {/* Cost Estimates */}
        <CostEstimateBox>
          <Grid container spacing={2}>
            <Grid item xs={4}>
              <EditableContent
                title="Cost Estimate"
                content={`$${executiveSummary.costEstimate.estimated.toLocaleString()}`}
                icon={CostIcon}
                onUpdate={(newData) => {
                  setExecutiveSummary(prev => ({
                    ...prev,
                    costEstimate: { 
                      ...prev.costEstimate, 
                      estimated: Number(newData.content?.replace(/[^0-9.-]+/g, '') || 0) 
                    },
                  }));
                }}
                onGenerate={() => handleGenerate('costEstimate')}
              />
            </Grid>
            <Grid item xs={4}>
              <EditableContent
                title="Solution Estimate"
                content={`$${executiveSummary.costEstimate.solution.toLocaleString()}`}
                icon={TargetIcon}
                onUpdate={(newData) => {
                  setExecutiveSummary(prev => ({
                    ...prev,
                    costEstimate: { 
                      ...prev.costEstimate, 
                      solution: Number(newData.content?.replace(/[^0-9.-]+/g, '') || 0) 
                    },
                  }));
                }}
                onGenerate={() => handleGenerate('solutionEstimate')}
              />
            </Grid>
            <Grid item xs={4}>
              <EditableContent
                title="Actual Cost"
                content={`$${executiveSummary.costEstimate.actual.toLocaleString()}`}
                icon={CostIcon}
                onUpdate={(newData) => {
                  setExecutiveSummary(prev => ({
                    ...prev,
                    costEstimate: { 
                      ...prev.costEstimate, 
                      actual: Number(newData.content?.replace(/[^0-9.-]+/g, '') || 0) 
                    },
                  }));
                }}
              />
            </Grid>
          </Grid>
        </CostEstimateBox>
      </Section>

      <Section>
        {/* Mission & Vision */}
        <EditableContent
          title={executiveSummary.mission.title}
          content={executiveSummary.mission.content}
          icon={executiveSummary.mission.icon}
          onUpdate={(newData) => {
            setExecutiveSummary(prev => ({
              ...prev,
              mission: { ...prev.mission, ...newData },
            }));
          }}
          onGenerate={() => handleGenerate('mission')}
        />
      </Section>

      <Section>
        {/* Key Objectives */}
        <ContentBox>
          <Typography variant="h6" sx={{ color: '#fff', mb: 2 }}>
            Key Objectives
          </Typography>
          <List>
            {executiveSummary.objectives.items.map((objective) => (
              <ListItem key={objective.id}>
                <EditableContent
                  title={`Objective ${objective.id}`}
                  content={objective.content}
                  icon={objective.icon}
                  onUpdate={(newData) => {
                    setExecutiveSummary(prev => ({
                      ...prev,
                      objectives: {
                        ...prev.objectives,
                        items: prev.objectives.items.map(item =>
                          item.id === objective.id ? { ...item, ...newData } : item
                        ),
                      },
                    }));
                  }}
                  onGenerate={() => handleGenerate(`objective_${objective.id}`)}
                />
              </ListItem>
            ))}
          </List>
        </ContentBox>
      </Section>

      <Section>
        {/* KPIs */}
        <ContentBox>
          <Typography variant="h6" sx={{ color: '#fff', mb: 2 }}>
            Key Performance Indicators
          </Typography>
          <List>
            {executiveSummary.kpis.items.map((kpi) => (
              <ListItem key={kpi.id}>
                <EditableContent
                  title={`KPI ${kpi.id}`}
                  content={kpi.content}
                  icon={kpi.icon}
                  onUpdate={(newData) => {
                    setExecutiveSummary(prev => ({
                      ...prev,
                      kpis: {
                        ...prev.kpis,
                        items: prev.kpis.items.map(item =>
                          item.id === kpi.id ? { ...item, ...newData } : item
                        ),
                      },
                    }));
                  }}
                  onGenerate={() => handleGenerate(`kpi_${kpi.id}`)}
                />
              </ListItem>
            ))}
          </List>
        </ContentBox>
      </Section>
    </Section>
  );

  const ProductRoadmapContent = () => (
    <Section>
      <GradientText gradient="primary" variant="h6" sx={{ mb: 2 }}>
        Product Roadmap
      </GradientText>

      {/* Product Vision & Strategy */}
      <EditableContent
        title={productRoadmap.vision.title}
        content={productRoadmap.vision.content}
        icon={productRoadmap.vision.icon}
        cost={productRoadmap.vision.cost}
        onUpdate={(newData) => {
          setProductRoadmap(prev => ({
            ...prev,
            vision: { ...prev.vision, ...newData },
          }));
        }}
        onGenerate={() => handleGenerate('productVision')}
      />

      {/* User Research & Personas */}
      <ContentBox>
        <Typography variant="h6" sx={{ color: '#fff', mb: 2 }}>
          User Research & Personas
        </Typography>
        <Grid container spacing={2}>
          {productRoadmap.userResearch.personas.map((persona) => (
            <Grid item xs={12} sm={12} md={6} key={persona.id}>
              <EditableContent
                title={persona.title}
                content={persona.description}
                icon={persona.icon}
                onUpdate={(newData) => {
                  setProductRoadmap(prev => ({
                    ...prev,
                    userResearch: {
                      ...prev.userResearch,
                      personas: prev.userResearch.personas.map(p =>
                        p.id === persona.id ? { ...p, ...newData } : p
                      ),
                    },
                  }));
                }}
                onGenerate={() => handleGenerate(`persona_${persona.id}`)}
              />
            </Grid>
          ))}
        </Grid>
      </ContentBox>

      {/* Quarterly Roadmap */}
      <ContentBox>
        <Typography variant="h6" sx={{ color: '#fff', mb: 2 }}>
          Quarterly Roadmap
        </Typography>
        <Grid container spacing={2}>
          {productRoadmap.roadmap.quarters.map((quarter) => (
            <Grid item xs={12} md={3} key={quarter.id}>
              <EditableContent
                title={`${quarter.id}: ${quarter.title}`}
                content={quarter.objectives.join('\n')}
                icon={quarter.icon}
                onUpdate={(newData) => {
                  setProductRoadmap(prev => ({
                    ...prev,
                    roadmap: {
                      ...prev.roadmap,
                      quarters: prev.roadmap.quarters.map(q =>
                        q.id === quarter.id ? {
                          ...q,
                          title: newData.title?.split(': ')[1] || q.title,
                          objectives: newData.content?.split('\n') || q.objectives,
                        } : q
                      ),
                    },
                  }));
                }}
                onGenerate={() => handleGenerate(`quarter_${quarter.id}`)}
              />
            </Grid>
          ))}
        </Grid>
      </ContentBox>

      {/* Feature Prioritization */}
      <ContentBox>
        <Typography variant="h6" sx={{ color: '#fff', mb: 2 }}>
          Feature Prioritization
        </Typography>
        {productRoadmap.features.categories.map((category) => (
          <Box key={category.id} sx={{ mb: 2 }}>
            <EditableContent
              title={category.title}
              content={category.items.join('\n')}
              icon={category.icon}
              onUpdate={(newData) => {
                setProductRoadmap(prev => ({
                  ...prev,
                  features: {
                    ...prev.features,
                    categories: prev.features.categories.map(c =>
                      c.id === category.id ? {
                        ...c,
                        title: newData.title || c.title,
                        items: newData.content?.split('\n') || c.items,
                      } : c
                    ),
                  },
                }));
              }}
              onGenerate={() => handleGenerate(`features_${category.id}`)}
            />
          </Box>
        ))}
      </ContentBox>

      {/* Agile Development Process */}
      <ContentBox>
        <Typography variant="h6" sx={{ color: '#fff', mb: 2 }}>
          Agile Development Process
        </Typography>
        {productRoadmap.agile.processes.map((process) => (
          <Box key={process.id} sx={{ mb: 2 }}>
            <EditableContent
              title={process.title}
              content={process.description}
              icon={process.icon}
              onUpdate={(newData) => {
                setProductRoadmap(prev => ({
                  ...prev,
                  agile: {
                    ...prev.agile,
                    processes: prev.agile.processes.map(p =>
                      p.id === process.id ? { ...p, ...newData } : p
                    ),
                  },
                }));
              }}
              onGenerate={() => handleGenerate(`agile_${process.id}`)}
            />
          </Box>
        ))}
      </ContentBox>
    </Section>
  );

  const TechnologyRoadmapContent = () => (
    <Section>
      <GradientText gradient="primary" variant="h6" sx={{ mb: 2 }}>
        Technology Roadmap
      </GradientText>

      {/* Technology Stack Overview */}
      <EditableContent
        title={techRoadmap.overview.title}
        content={techRoadmap.overview.content}
        icon={techRoadmap.overview.icon}
        cost={techRoadmap.overview.cost}
        onUpdate={(newData) => {
          setTechRoadmap(prev => ({
            ...prev,
            overview: { ...prev.overview, ...newData },
          }));
        }}
        onGenerate={() => handleGenerate('techOverview')}
      />

      {/* Frontend Architecture */}
      <ContentBox>
        <Typography variant="h6" sx={{ color: '#fff', mb: 2 }}>
          Frontend Architecture
        </Typography>
        <Grid container spacing={2}>
          {techRoadmap.frontend.stack.map((item) => (
            <Grid item xs={12} md={6} key={item.id}>
              <EditableContent
                title={item.name}
                content={item.description}
                icon={item.icon}
                onUpdate={(newData) => {
                  setTechRoadmap(prev => ({
                    ...prev,
                    frontend: {
                      ...prev.frontend,
                      stack: prev.frontend.stack.map(s =>
                        s.id === item.id ? { ...s, ...newData } : s
                      ),
                    },
                  }));
                }}
                onGenerate={() => handleGenerate(`frontend_${item.id}`)}
              />
            </Grid>
          ))}
        </Grid>
      </ContentBox>

      {/* Backend Infrastructure */}
      <ContentBox>
        <Typography variant="h6" sx={{ color: '#fff', mb: 2 }}>
          Backend Infrastructure
        </Typography>
        <Grid container spacing={2}>
          {techRoadmap.backend.stack.map((item) => (
            <Grid item xs={12} md={6} key={item.id}>
              <EditableContent
                title={item.name}
                content={item.description}
                icon={item.icon}
                onUpdate={(newData) => {
                  setTechRoadmap(prev => ({
                    ...prev,
                    backend: {
                      ...prev.backend,
                      stack: prev.backend.stack.map(s =>
                        s.id === item.id ? { ...s, ...newData } : s
                      ),
                    },
                  }));
                }}
                onGenerate={() => handleGenerate(`backend_${item.id}`)}
              />
            </Grid>
          ))}
        </Grid>
      </ContentBox>

      {/* Cloud Infrastructure */}
      <ContentBox>
        <Typography variant="h6" sx={{ color: '#fff', mb: 2 }}>
          Cloud Infrastructure
        </Typography>
        <Grid container spacing={2}>
          {techRoadmap.infrastructure.components.map((item) => (
            <Grid item xs={12} md={6} key={item.id}>
              <EditableContent
                title={item.name}
                content={item.description}
                icon={item.icon}
                onUpdate={(newData) => {
                  setTechRoadmap(prev => ({
                    ...prev,
                    infrastructure: {
                      ...prev.infrastructure,
                      components: prev.infrastructure.components.map(c =>
                        c.id === item.id ? { ...c, ...newData } : c
                      ),
                    },
                  }));
                }}
                onGenerate={() => handleGenerate(`infrastructure_${item.id}`)}
              />
            </Grid>
          ))}
        </Grid>
      </ContentBox>

      {/* Security & Compliance */}
      <ContentBox>
        <Typography variant="h6" sx={{ color: '#fff', mb: 2 }}>
          Security & Compliance
        </Typography>
        {techRoadmap.security.measures.map((measure) => (
          <Box key={measure.id} sx={{ mb: 2 }}>
            <EditableContent
              title={measure.name}
              content={measure.description}
              icon={measure.icon}
              onUpdate={(newData) => {
                setTechRoadmap(prev => ({
                  ...prev,
                  security: {
                    ...prev.security,
                    measures: prev.security.measures.map(m =>
                      m.id === measure.id ? { ...m, ...newData } : m
                    ),
                  },
                }));
              }}
              onGenerate={() => handleGenerate(`security_${measure.id}`)}
            />
          </Box>
        ))}
      </ContentBox>

      {/* Performance Optimization */}
      <ContentBox>
        <Typography variant="h6" sx={{ color: '#fff', mb: 2 }}>
          Performance Optimization
        </Typography>
        {techRoadmap.performance.strategies.map((strategy) => (
          <Box key={strategy.id} sx={{ mb: 2 }}>
            <EditableContent
              title={strategy.name}
              content={strategy.description}
              icon={strategy.icon}
              onUpdate={(newData) => {
                setTechRoadmap(prev => ({
                  ...prev,
                  performance: {
                    ...prev.performance,
                    strategies: prev.performance.strategies.map(s =>
                      s.id === strategy.id ? { ...s, ...newData } : s
                    ),
                  },
                }));
              }}
              onGenerate={() => handleGenerate(`performance_${strategy.id}`)}
            />
          </Box>
        ))}
      </ContentBox>

      {/* DevOps & CI/CD */}
      <ContentBox>
        <Typography variant="h6" sx={{ color: '#fff', mb: 2 }}>
          DevOps & CI/CD
        </Typography>
        {techRoadmap.devops.pipeline.map((item) => (
          <Box key={item.id} sx={{ mb: 2 }}>
            <EditableContent
              title={item.name}
              content={item.description}
              icon={item.icon}
              onUpdate={(newData) => {
                setTechRoadmap(prev => ({
                  ...prev,
                  devops: {
                    ...prev.devops,
                    pipeline: prev.devops.pipeline.map(p =>
                      p.id === item.id ? { ...p, ...newData } : p
                    ),
                  },
                }));
              }}
              onGenerate={() => handleGenerate(`devops_${item.id}`)}
            />
          </Box>
        ))}
      </ContentBox>
    </Section>
  );

  const MarketStrategyAnalysisContent = () => (
    <Section>
      <GradientText gradient="primary" variant="h6" sx={{ mb: 2 }}>
        Market Strategy & Analysis
      </GradientText>

      {/* Market Strategy Section */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h6" sx={{ color: '#00CC99', mb: 2 }}>
          Market Strategy
        </Typography>

        {/* Strategy Overview */}
        <EditableContent
          title={marketStrategy.overview.title}
          content={marketStrategy.overview.content}
          icon={marketStrategy.overview.icon}
          cost={marketStrategy.overview.cost}
          onUpdate={(newData) => {
            setMarketStrategy(prev => ({
              ...prev,
              overview: { ...prev.overview, ...newData },
            }));
          }}
          onGenerate={() => handleGenerate('marketStrategyOverview')}
        />

        {/* Market Positioning */}
        <ContentBox>
          <Typography variant="h6" sx={{ color: '#fff', mb: 2 }}>
            Market Positioning
          </Typography>
          <Grid container spacing={2}>
            {marketStrategy.positioning.strategies.map((strategy) => (
              <Grid item xs={12} md={6} key={strategy.id}>
                <EditableContent
                  title={strategy.name}
                  content={strategy.description}
                  icon={strategy.icon}
                  onUpdate={(newData) => {
                    setMarketStrategy(prev => ({
                      ...prev,
                      positioning: {
                        ...prev.positioning,
                        strategies: prev.positioning.strategies.map(s =>
                          s.id === strategy.id ? { ...s, ...newData } : s
                        ),
                      },
                    }));
                  }}
                  onGenerate={() => handleGenerate(`positioning_${strategy.id}`)}
                />
              </Grid>
            ))}
          </Grid>
        </ContentBox>

        {/* Marketing Channels */}
        <ContentBox>
          <Typography variant="h6" sx={{ color: '#fff', mb: 2 }}>
            Marketing Channels
          </Typography>
          {marketStrategy.channels.strategies.map((channel) => (
            <Box key={channel.id} sx={{ mb: 2 }}>
              <EditableContent
                title={channel.name}
                content={channel.description}
                icon={channel.icon}
                onUpdate={(newData) => {
                  setMarketStrategy(prev => ({
                    ...prev,
                    channels: {
                      ...prev.channels,
                      strategies: prev.channels.strategies.map(c =>
                        c.id === channel.id ? { ...c, ...newData } : c
                      ),
                    },
                  }));
                }}
                onGenerate={() => handleGenerate(`channel_${channel.id}`)}
              />
            </Box>
          ))}
        </ContentBox>
      </Box>

      {/* Market Analysis Section */}
      <Box>
        <Typography variant="h6" sx={{ color: '#00CC99', mb: 2 }}>
          Market Analysis
        </Typography>

        {/* Analysis Overview */}
        <EditableContent
          title={marketStrategy.marketAnalysis.overview.title}
          content={marketStrategy.marketAnalysis.overview.content}
          icon={marketStrategy.marketAnalysis.overview.icon}
          cost={marketStrategy.marketAnalysis.overview.cost}
          onUpdate={(newData) => {
            setMarketStrategy(prev => ({
              ...prev,
              marketAnalysis: {
                ...prev.marketAnalysis,
                overview: { ...prev.marketAnalysis.overview, ...newData },
              },
            }));
          }}
          onGenerate={() => handleGenerate('marketAnalysisOverview')}
        />

        {/* Target Market Analysis */}
        <ContentBox>
          <Typography variant="h6" sx={{ color: '#fff', mb: 2 }}>
            Target Market Analysis
          </Typography>
          <Grid container spacing={2}>
            {marketStrategy.marketAnalysis.targetMarket.segments.map((segment) => (
              <Grid item xs={12} md={6} key={segment.id}>
                <EditableContent
                  title={segment.name}
                  content={`${segment.description}\nMarket Size: ${segment.size}\nGrowth Rate: ${segment.growth}`}
                  icon={segment.icon}
                  onUpdate={(newData) => {
                    setMarketStrategy(prev => ({
                      ...prev,
                      marketAnalysis: {
                        ...prev.marketAnalysis,
                        targetMarket: {
                          ...prev.marketAnalysis.targetMarket,
                          segments: prev.marketAnalysis.targetMarket.segments.map(s =>
                            s.id === segment.id ? { ...s, ...newData } : s
                          ),
                        },
                      },
                    }));
                  }}
                  onGenerate={() => handleGenerate(`targetMarket_${segment.id}`)}
                />
              </Grid>
            ))}
          </Grid>
        </ContentBox>

        {/* Competitive Analysis */}
        <ContentBox>
          <Typography variant="h6" sx={{ color: '#fff', mb: 2 }}>
            Competitive Analysis
          </Typography>
          {marketStrategy.marketAnalysis.competition.competitors.map((competitor) => (
            <Box key={competitor.id} sx={{ mb: 2 }}>
              <EditableContent
                title={competitor.name}
                content={`${competitor.description}\n\nAnalysis:\n${competitor.analysis}`}
                icon={competitor.icon}
                onUpdate={(newData) => {
                  setMarketStrategy(prev => ({
                    ...prev,
                    marketAnalysis: {
                      ...prev.marketAnalysis,
                      competition: {
                        ...prev.marketAnalysis.competition,
                        competitors: prev.marketAnalysis.competition.competitors.map(c =>
                          c.id === competitor.id ? { ...c, ...newData } : c
                        ),
                      },
                    },
                  }));
                }}
                onGenerate={() => handleGenerate(`competitor_${competitor.id}`)}
              />
            </Box>
          ))}
        </ContentBox>

        {/* Market Segmentation */}
        <ContentBox>
          <Typography variant="h6" sx={{ color: '#fff', mb: 2 }}>
            Market Segmentation
          </Typography>
          <Grid container spacing={2}>
            {marketStrategy.marketAnalysis.segmentation.segments.map((segment) => (
              <Grid item xs={12} md={6} key={segment.id}>
                <EditableContent
                  title={segment.name}
                  content={segment.description}
                  icon={segment.icon}
                  onUpdate={(newData) => {
                    setMarketStrategy(prev => ({
                      ...prev,
                      marketAnalysis: {
                        ...prev.marketAnalysis,
                        segmentation: {
                          ...prev.marketAnalysis.segmentation,
                          segments: prev.marketAnalysis.segmentation.segments.map(s =>
                            s.id === segment.id ? { ...s, ...newData } : s
                          ),
                        },
                      },
                    }));
                  }}
                  onGenerate={() => handleGenerate(`segmentation_${segment.id}`)}
                />
              </Grid>
            ))}
          </Grid>
        </ContentBox>
      </Box>
    </Section>
  );

  // Add Financial Plan Content component
  const FinancialPlanContent = () => (
    <Section>
      <GradientText gradient="primary" variant="h6" sx={{ mb: 2 }}>
        Financial Plan
      </GradientText>

      {/* Financial Overview */}
      <EditableContent
        title={financialPlan.overview.title}
        content={financialPlan.overview.content}
        icon={financialPlan.overview.icon}
        cost={financialPlan.overview.cost}
        onUpdate={(newData) => {
          setFinancialPlan(prev => ({
            ...prev,
            overview: { ...prev.overview, ...newData },
          }));
        }}
        onGenerate={() => handleGenerate('financialOverview')}
      />

      {/* Revenue Projections */}
      <ContentBox>
        <Typography variant="h6" sx={{ color: '#fff', mb: 2 }}>
          Revenue Projections
        </Typography>
        <Grid container spacing={2}>
          {financialPlan.revenue.projections.map((projection) => (
            <Grid item xs={12} md={6} key={projection.id}>
              <EditableContent
                title={projection.period}
                content={`Amount: $${projection.amount.toLocaleString()}\nGrowth: ${projection.growth}\n\n${projection.description}`}
                icon={projection.icon}
                onUpdate={(newData) => {
                  setFinancialPlan(prev => ({
                    ...prev,
                    revenue: {
                      ...prev.revenue,
                      projections: prev.revenue.projections.map(p =>
                        p.id === projection.id ? { ...p, ...newData } : p
                      ),
                    },
                  }));
                }}
                onGenerate={() => handleGenerate(`revenue_${projection.id}`)}
              />
            </Grid>
          ))}
        </Grid>
      </ContentBox>

      {/* Cost Structure */}
      <ContentBox>
        <Typography variant="h6" sx={{ color: '#fff', mb: 2 }}>
          Cost Structure
        </Typography>
        {financialPlan.costs.categories.map((category) => (
          <Box key={category.id} sx={{ mb: 2 }}>
            <EditableContent
              title={category.name}
              content={category.items.map(item => `${item.name}: $${item.amount.toLocaleString()}`).join('\n')}
              icon={category.icon}
              onUpdate={(newData) => {
                setFinancialPlan(prev => ({
                  ...prev,
                  costs: {
                    ...prev.costs,
                    categories: prev.costs.categories.map(c =>
                      c.id === category.id ? { ...c, ...newData } : c
                    ),
                  },
                }));
              }}
              onGenerate={() => handleGenerate(`costs_${category.id}`)}
            />
          </Box>
        ))}
      </ContentBox>

      {/* Funding Requirements */}
      <ContentBox>
        <Typography variant="h6" sx={{ color: '#fff', mb: 2 }}>
          Funding Requirements
        </Typography>
        {financialPlan.funding.rounds.map((round) => (
          <Box key={round.id} sx={{ mb: 2 }}>
            <EditableContent
              title={round.name}
              content={`Amount: $${round.amount.toLocaleString()}\n\n${round.description}`}
              icon={round.icon}
              onUpdate={(newData) => {
                setFinancialPlan(prev => ({
                  ...prev,
                  funding: {
                    ...prev.funding,
                    rounds: prev.funding.rounds.map(r =>
                      r.id === round.id ? { ...r, ...newData } : r
                    ),
                  },
                }));
              }}
              onGenerate={() => handleGenerate(`funding_${round.id}`)}
            />
          </Box>
        ))}
      </ContentBox>

      {/* Financial Metrics */}
      <ContentBox>
        <Typography variant="h6" sx={{ color: '#fff', mb: 2 }}>
          Financial Metrics
        </Typography>
        <Grid container spacing={2}>
          {financialPlan.metrics.kpis.map((kpi) => (
            <Grid item xs={12} md={6} key={kpi.id}>
              <EditableContent
                title={kpi.name}
                content={`Value: ${kpi.value}\n\n${kpi.description}`}
                icon={kpi.icon}
                onUpdate={(newData) => {
                  setFinancialPlan(prev => ({
                    ...prev,
                    metrics: {
                      ...prev.metrics,
                      kpis: prev.metrics.kpis.map(k =>
                        k.id === kpi.id ? { ...k, ...newData } : k
                      ),
                    },
                  }));
                }}
                onGenerate={() => handleGenerate(`metrics_${kpi.id}`)}
              />
            </Grid>
          ))}
        </Grid>
      </ContentBox>
    </Section>
  );

  // Add Company & Team Content component
  const CompanyTeamContent = () => (
    <Section>
      <GradientText gradient="primary" variant="h6" sx={{ mb: 2 }}>
        Company & Team
      </GradientText>

      {/* Company & Team Overview */}
      <EditableContent
        title={companyTeam.overview.title}
        content={companyTeam.overview.content}
        icon={companyTeam.overview.icon}
        cost={companyTeam.overview.cost}
        onUpdate={(newData) => {
          setCompanyTeam(prev => ({
            ...prev,
            overview: { ...prev.overview, ...newData },
          }));
        }}
        onGenerate={() => handleGenerate('companyOverview')}
      />

      {/* Leadership Team */}
      <ContentBox>
        <Typography variant="h6" sx={{ color: '#fff', mb: 2 }}>
          Leadership Team
        </Typography>
        {companyTeam.leadership.members.map((member) => (
          <Box key={member.id} sx={{ mb: 2 }}>
            <EditableContent
              title={`${member.name} - ${member.role}`}
              content={`${member.bio}\n\nKey Achievements:\n${member.achievements.join('\n')}`}
              icon={member.icon}
              onUpdate={(newData) => {
                setCompanyTeam(prev => ({
                  ...prev,
                  leadership: {
                    ...prev.leadership,
                    members: prev.leadership.members.map(m =>
                      m.id === member.id ? { ...m, ...newData } : m
                    ),
                  },
                }));
              }}
              onGenerate={() => handleGenerate(`leadership_${member.id}`)}
            />
          </Box>
        ))}
      </ContentBox>

      {/* Department Structure */}
      <ContentBox>
        <Typography variant="h6" sx={{ color: '#fff', mb: 2 }}>
          Department Structure
        </Typography>
        <Grid container spacing={2}>
          {companyTeam.departments.teams.map((team) => (
            <Grid item xs={12} md={6} key={team.id}>
              <EditableContent
                title={team.name}
                content={`Headcount: ${team.headCount}\n\nKey Roles:\n${team.roles.join('\n')}`}
                icon={team.icon}
                onUpdate={(newData) => {
                  setCompanyTeam(prev => ({
                    ...prev,
                    departments: {
                      ...prev.departments,
                      teams: prev.departments.teams.map(t =>
                        t.id === team.id ? { ...t, ...newData } : t
                      ),
                    },
                  }));
                }}
                onGenerate={() => handleGenerate(`department_${team.id}`)}
              />
            </Grid>
          ))}
        </Grid>
      </ContentBox>

      {/* Hiring Plan */}
      <ContentBox>
        <Typography variant="h6" sx={{ color: '#fff', mb: 2 }}>
          Hiring Plan
        </Typography>
        {companyTeam.hiring.plans.map((plan) => (
          <Box key={plan.id} sx={{ mb: 2 }}>
            <EditableContent
              title={plan.period}
              content={`Budget: $${plan.budget.toLocaleString()}\n\nOpen Roles:\n${plan.roles.join('\n')}`}
              icon={plan.icon}
              onUpdate={(newData) => {
                setCompanyTeam(prev => ({
                  ...prev,
                  hiring: {
                    ...prev.hiring,
                    plans: prev.hiring.plans.map(p =>
                      p.id === plan.id ? { ...p, ...newData } : p
                    ),
                  },
                }));
              }}
              onGenerate={() => handleGenerate(`hiring_${plan.id}`)}
            />
          </Box>
        ))}
      </ContentBox>

      {/* Company Culture & Values */}
      <ContentBox>
        <Typography variant="h6" sx={{ color: '#fff', mb: 2 }}>
          Company Culture & Values
        </Typography>
        <Grid container spacing={2}>
          {companyTeam.culture.values.map((value) => (
            <Grid item xs={12} md={6} key={value.id}>
              <EditableContent
                title={value.name}
                content={value.description}
                icon={value.icon}
                onUpdate={(newData) => {
                  setCompanyTeam(prev => ({
                    ...prev,
                    culture: {
                      ...prev.culture,
                      values: prev.culture.values.map(v =>
                        v.id === value.id ? { ...v, ...newData } : v
                      ),
                    },
                  }));
                }}
                onGenerate={() => handleGenerate(`culture_${value.id}`)}
              />
            </Grid>
          ))}
        </Grid>
      </ContentBox>
    </Section>
  );

  // Update planSteps to include new steps
  const planSteps: StepData[] = [
    {
      label: 'Executive Summary',
      description: 'Overview, costs, mission, objectives, and KPIs',
      icon: <FileTextIcon />,
      content: <ExecutiveSummaryContent />
    },
    {
      label: 'Technology Roadmap',
      description: 'Technical architecture and implementation strategy',
      icon: <TechIcon />,
      content: <TechnologyRoadmapContent />
    },
    {
        label: 'Product Roadmap',
        description: 'Product vision, features, and development strategy',
        icon: <ProductIcon />,
        content: <ProductRoadmapContent />
      },
    {
      label: 'Market Strategy & Analysis',
      description: 'Market strategy, positioning, and detailed market analysis',
      icon: <MarketIcon />,
      content: <MarketStrategyAnalysisContent />
    },
    {
      label: 'Financial Plan',
      description: 'Revenue, costs, funding, and financial metrics',
      icon: <CostIcon />,
      content: <FinancialPlanContent />
    },
    {
      label: 'Company & Team',
      description: 'Organization structure, team members, and company culture',
      icon: <TeamIcon />,
      content: <CompanyTeamContent />
    },
  ];

  return (
    <StepperContainer>
      <Box sx={{ mb: 4, pb: 2, borderBottom: '1px solid rgba(138, 43, 226, 0.2)' }}>
        <HolographicStepper
          steps={planSteps}
          initialStep={initialStep}
          onComplete={onComplete}
          onSubmitRFP={onSubmitRFP}
          orientation="horizontal"
          alternativeLabel={true}
          onChange={(newStep) => setActiveStep(newStep)}
        />
      </Box>
      
      <Box sx={{ height: '20px' }} />
      
      <ContentCard>
        {planSteps[activeStep || initialStep].content}
      </ContentCard>
    </StepperContainer>
  );
};

export default PlanStepper; 